/**
 * Personnel Intelligence System
 * PSP/CRE Tool v3.0 - Calculation Engine
 * 
 * REFACTORED VERSION - Imports from constants (no hardcoded values)
 * 
 * Purpose: Intelligent personnel management including:
 * - Medical personnel calculation
 * - Casualty risk modeling
 * - Replacement advisor (guided mode)
 * - Reserve pool management
 * - RTD projection integration
 * - CSR casualty projection
 * 
 * Author: Dr. Tatar Radu MD
 * Date: December 14, 2024
 * Version: 2.14 - BUG-082: calculatePersonnelAdequacy now resolves facilities via the same
 *   4-path chain as calculateBedOccupancyAnalysis (Tab 3 saves to medical_configuration.facilities,
 *   not mtf_list — function was always receiving [] → required=0 → 100% ADEQUATE). Role-type
 *   matchers expanded to cover role_type and mtf_role fields. (March 12, 2026)
 *
 * Version: 2.13 - BED-D-005: Per-role breakdown cards broken when CSU/PHA beds present — baseBedSource fix (March 4, 2026)
 *   Root cause: bedSource mutated to '...facilities + 90 CSU/PHA beds' before per-role dispatch,
 *   breaking all strict === equality checks. Fix: capture baseBedSource before CSU/PHA append.
 * Version: 2.12 - CSU-PHA-BED: Add CSU + PHA capacity_patients to totalBeds in calculateBedOccupancyAnalysis (March 4, 2026)
 * Version: 2.11 - BED-D-004: Exclude Role 1 facilities from Priority 1/2/3 bed totals (AJMedP-1) (March 3, 2026)
 * Version: 2.10 - BED-D-001/002/003: Per-role bed counts now derived from same data path as system-wide (March 3, 2026)
 * Version: 2.9 - BED-OCC: Per-role bed occupancy breakdown; HOSPITALIZATION_RATE + ECHELON_ADMISSION_DISTRIBUTION
 *   imported from theatreHoldingPolicy.js; inline 0.35 literal replaced (APP_RULES 2.1) (March 3, 2026)
 * Version: 2.7 - APP_RULES 6.1 Compliance: Remove all ?? and || fallbacks from constants lookups (February 22, 2026)
 *
 * CHANGELOG v2.9 (March 12, 2026):
 * - BUG-088 / BUG-073 survivors: Removed 4 console.log statements that fired on
 *   every Tab3/Tab4 entry: RTD fallback (line ~720), Bed Occupancy fallback estimate
 *   (line ~1339), CSU/PHA staging beds count (line ~1381), Bed Occupancy Analysis
 *   dump (lines ~1430-1439). All console.error and console.warn entries preserved.
 *
 * CHANGELOG v2.8 (February 25, 2026):
 * - FIX (APP_RULES 6.1): calculateBedOccupancyAnalysis() mtf_structure path:
 *   role1 fallback was || 10 (wrong — doctrine = 6, non-holding per AJMedP-1). Changed to || 0.
 *   role2 fallback corrected from || 50 to || 30 (matches ROLE_2_BASIC_DEFINITION.holding_capacity.beds).
 *
 * CHANGELOG v2.7 (February 22, 2026):
 * - APP_RULES 6.1/6.3 FIX: Removed all ?? and || fallbacks on constants lookups.
 *   Fallbacks mask validation errors per APP_RULES 6.1 — missing constants must throw.
 * - FIXED lines 723–726: t1/t2/t3/t4RtdRate ?? numeric fallbacks → guard throw on undefined.
 *   Same pattern applied as in casualtyCalculator.js v5.4.7.
 * - FIXED lines 705–708: EVACUATION_PRIORITIES?.T* || 'string' fallbacks → throw on undefined.
 * - FIXED lines 734–752: PATIENT_DISPOSITION?.*.code || 'string' fallbacks → throw on undefined.
 * - Removed: @institution Romanian Military Medical Institute (APP_RULES 5.3).
 * 
 * CHANGELOG v2.6 (February 22, 2026):
 * - FIX P1-04: calculateRTDProjection() used RTD_RATES?.T1/T2/T3/T4 paths — all undefined.
 *   RTD_RATES structure is BY_TRIAGE_CATEGORY.T1_IMMEDIATE.rtd_rates[timeframe].
 *   The ?.T1 shortcut path does not exist → fallbacks 0.05/0.15/0.60/0.02 fired 100% of the time.
 *   Fix: derive rtdTimeframe from thpDays, then read correct nested path per triage category.
 *   Disposition strings updated to use PATIENT_DISPOSITION object keys (RTD/HOLD_THEATRE/DIED).
 *
 * CHANGELOG v2.5 (January 17, 2026):
 * - FIX: calculateBedOccupancyAnalysis() was returning 135%+ for normal operations
 *   - ROOT CAUSE: Looking at wrong path (mtf_structure) - Tab 3 saves to facilities/mtf_list
 *   - ROOT CAUSE: Missing Role 1 beds (only counted R2, R2E, R3)
 *   - ROOT CAUSE: totalBeds = 0 caused division by zero → infinity% occupancy
 * - FIX: Now checks 5 different data paths for facilities:
 *   1. medical_configuration.facilities (Tab 3 primary path)
 *   2. medical_configuration.mtf_list
 *   3. force.forceBuilderResult.facilities
 *   4. medical_configuration.mtf_structure (legacy)
 *   5. Fallback estimation based on force echelon
 * - FIX: Now counts ALL beds including Role 1
 * - FIX: Returns realistic 40-70% occupancy for normal corps operations
 * - ADD: Detailed debug logging for troubleshooting
 *
 * CHANGELOG v2.4:
 * - UPDATE: calculateReservePoolDepletion() now uses personnel_arrivals schedule
 *   - Reads scenario.logistics.personnel_arrivals[] from Tab 4
 *   - Adds arrivals to reserve pool on scheduled day by type (COMBAT/MEDICAL)
 *   - Tracks total_arrivals in summary and arrivals_today in daily_status
 *   - Essential for realistic 14+ day operations planning
 *
 * CHANGELOG v2.3:
 * - FIX: calculateRTDProjection() now generates fallback estimates when casualties = 0
 *   - Uses historical rates: 0.5% daily battle + 0.3% DNBI casualties
 *   - Triage distribution based on Ukraine 2022-2024 data
 *   - Returns is_estimated: true and estimation_note for UI display
 *   - Prevents RTD showing 0% before Tab 5 calculation runs
 *
 * CHANGELOG v2.2:
 * - FIX: calculateCSRProjection() was calling calculateCSRCasualties() with wrong signature
 *   - WRONG: Called with object { forceStrength, combatIntensity, ... }
 *   - RIGHT: Positional params (forceStrength, intensity, days, riskFactors, protectiveFactors)
 * - FIX: Force strength property name - Tab1 saves as 'totalStrength' not 'total_strength'
 * - Added proper risk factors and protective factors mapping from scenario data
 *
 * Updated: December 18, 2024
 * 
 * ARCHITECTURE:
 * - RTD rates from theatreHoldingPolicy.js
 * - CSR rates from mentalHealth.js
 * - Medical personnel standards from mtfCapabilities.js
 * - Casualty rates from casualtyCalculatorConstants.js
 * - No hardcoded values in this file
 * 
 * German user example: Updates nationalStandards.js, ZERO changes here
 * 
 * CHANGELOG v2.1:
 * - Fixed MTF_CAPABILITIES import error (was importing non-existent export)
 * - Added ROLE_TO_ECHELON_MAP for proper echelon-based staffing lookup
 * - Moved hardcoded exposure rates to casualtyCalculatorConstants.js
 * - Moved hardcoded threat modifiers to casualtyCalculatorConstants.js
 * - Moved replacement planning constants to casualtyCalculatorConstants.js
 * - Added CSR thresholds import
 */

// ============================================================================
// IMPORTS FROM CONSTANTS
// ============================================================================

// CALC-010: required-constant reader (throws with the path instead of a literal fallback)
// CALC-010: documented default + "assumptions used" ledger instead of a silent literal fallback
import { resolvePsychiatricSupport, PSYCHIATRIC_SUPPORT_LEVELS } from '../../constants/psychiatricSupport.js';
import { resolveInput } from '../assumptionLedger.js';

import { requireNumber } from '../requireValue.js';

import {
  THEATRE_HOLDING_POLICY,
  RTD_RATES,
  EVACUATION_PRIORITIES,
  PATIENT_DISPOSITION,
  calculateRTD as constantsCalculateRTD,
  calculateBedOccupancy,
  getEvacuationPriority,
  // v2.9 BED-OCC: constants for per-role bed occupancy breakdown
  HOSPITALIZATION_RATE,
  ECHELON_ADMISSION_DISTRIBUTION
} from '../../constants/theatreHoldingPolicy';

import {
  CSR_RATES,
  MENTAL_HEALTH_SUPPORT,
  BICEPS_PRINCIPLES,
  calculateCSRCasualties,
  calculateMentalHealthSupport
} from '../../constants/mentalHealth';

// FIXED: Correct imports from mtfCapabilities.js
import {
  MTF_CAPABILITIES_BY_ECHELON,
  getMTFCapabilities,
  getPersonnelBreakdown
} from '../../constants/mtfCapabilities';

import {
  NATIONAL_STANDARDS,
  getStandardConfig
} from '../../constants/nationalStandards';

// NEW: Import casualty calculator constants for exposure rates, threat modifiers, etc.
import {
  PERSONNEL_CASUALTY_DISTRIBUTION,
  MEDICAL_EXPOSURE_RATES,
  THREAT_LEVEL_MODIFIERS,
  REPLACEMENT_PLANNING,
  CSR_THRESHOLDS,
  getThreatModifier,
  getMedicalExposureRate,
  parseArrivalTime
} from '../../constants/casualtyCalculatorConstants';

// ============================================================================
// ROLE TO ECHELON MAPPING
// Maps MTF role levels to their corresponding echelon and role key
// ============================================================================

const ROLE_TO_ECHELON_MAP = {
  ROLE_1: {
    echelon: 'BATTALION',
    roleKey: 'role1',
    description: 'Battalion Aid Station'
  },
  ROLE_2_BASIC: {
    echelon: 'BRIGADE',
    roleKey: 'role2',
    description: 'Forward Resuscitation (Brigade level)'
  },
  ROLE_2_ENHANCED: {
    echelon: 'DIVISION',
    roleKey: 'role2',
    description: 'Forward Surgical Team (Division level)'
  },
  ROLE_3: {
    echelon: 'CORPS',
    roleKey: 'role3',
    description: 'Combat Support Hospital'
  }
};

// ============================================================================
// MEDICAL PERSONNEL CALCULATION
// ============================================================================

/**
 * Calculate total medical personnel from all sources
 * Uses MTF_CAPABILITIES_BY_ECHELON from constants for personnel-per-facility ratios
 * 
 * @param {object} scenario - Complete scenario object
 * @returns {object} Medical personnel breakdown
 */
export function calculateMedicalPersonnel(scenario) {
  const personnel = {
    mtf_personnel: {
      medical_officers: 0,
      nurses: 0,
      medics: 0,
      surgeons: 0,
      anesthesiologists: 0,
      support_staff: 0,
      total: 0
    },
    staging_facility_personnel: {
      medical_officers: 0,
      nurses: 0,
      medics: 0,
      support_staff: 0,
      total: 0
    },
    hlz_personnel: {
      landing_zone_officers: 0,
      air_traffic_controllers: 0,
      ground_crew: 0,
      security: 0,
      total: 0
    },
    mental_health_personnel: {
      psychiatrists: 0,
      psychologists: 0,
      social_workers: 0,
      chaplains: 0,
      total: 0
    },
    total_medical_personnel: 0,
    // Include national standards reference
    national_standards_applied: null,
    national_standards_reference: NATIONAL_STANDARDS
  };

  try {
    const nationalStandard = scenario?.force?.national_standard || 'NATO';
    const standardConfig = getStandardConfig ? getStandardConfig(nationalStandard) : null;
    
    // Store which national standard is being applied
    personnel.national_standards_applied = {
      standard: nationalStandard,
      config: standardConfig,
      available_standards: NATIONAL_STANDARDS ? Object.keys(NATIONAL_STANDARDS) : ['NATO', 'US', 'RO']
    };

    // 1. MTF Personnel (using constants)
    if (scenario?.medical_configuration?.mtf_structure) {
      personnel.mtf_personnel = estimateMTFPersonnel(
        scenario.medical_configuration.mtf_structure,
        standardConfig
      );
    }

    // 2. CSU/PHA Personnel (from Tab 3)
    if (scenario?.medical_configuration?.staging_facilities) {
      scenario.medical_configuration.staging_facilities.forEach(facility => {
        if (facility.personnel) {
          personnel.staging_facility_personnel.medical_officers += facility.personnel.medical_officers || 0;
          personnel.staging_facility_personnel.nurses += facility.personnel.nurses || 0;
          personnel.staging_facility_personnel.medics += facility.personnel.medics || 0;
          personnel.staging_facility_personnel.support_staff += facility.personnel.support_staff || 0;
        }
      });
      
      personnel.staging_facility_personnel.total = 
        personnel.staging_facility_personnel.medical_officers +
        personnel.staging_facility_personnel.nurses +
        personnel.staging_facility_personnel.medics +
        personnel.staging_facility_personnel.support_staff;
    }

    // 3. HLZ Personnel (from Tab 3)
    if (scenario?.medical_configuration?.helicopter_facilities) {
      scenario.medical_configuration.helicopter_facilities.forEach(hlz => {
        if (hlz.personnel) {
          personnel.hlz_personnel.landing_zone_officers += hlz.personnel.landing_zone_officers || 0;
          personnel.hlz_personnel.air_traffic_controllers += hlz.personnel.air_traffic_controllers || 0;
          personnel.hlz_personnel.ground_crew += hlz.personnel.ground_crew || 0;
          personnel.hlz_personnel.security += hlz.personnel.security || 0;
        }
      });
      
      personnel.hlz_personnel.total = 
        personnel.hlz_personnel.landing_zone_officers +
        personnel.hlz_personnel.air_traffic_controllers +
        personnel.hlz_personnel.ground_crew +
        personnel.hlz_personnel.security;
    }

    // 4. Mental Health Personnel (using MENTAL_HEALTH_SUPPORT from constants)
    if (scenario?.logistics?.mental_health) {
      const mh = scenario.logistics.mental_health;
      personnel.mental_health_personnel.psychiatrists = mh.psychiatrists || 0;
      personnel.mental_health_personnel.psychologists = mh.psychologists || 0;
      personnel.mental_health_personnel.social_workers = mh.social_workers || 0;
      personnel.mental_health_personnel.chaplains = mh.chaplains || 0;
      
      personnel.mental_health_personnel.total = 
        personnel.mental_health_personnel.psychiatrists +
        personnel.mental_health_personnel.psychologists +
        personnel.mental_health_personnel.social_workers +
        personnel.mental_health_personnel.chaplains;
    }

    // Calculate total
    personnel.total_medical_personnel = 
      personnel.mtf_personnel.total +
      personnel.staging_facility_personnel.total +
      personnel.hlz_personnel.total +
      personnel.mental_health_personnel.total;

    return personnel;
  } catch (error) {
    console.error('Error calculating medical personnel:', error);
    return personnel;
  }
}

/**
 * Estimate MTF personnel from capabilities
 * Uses MTF_CAPABILITIES_BY_ECHELON from constants for standard staffing ratios
 */
function estimateMTFPersonnel(mtfStructure, standardConfig = null) {
  const personnel = {
    medical_officers: 0,
    nurses: 0,
    medics: 0,
    surgeons: 0,
    anesthesiologists: 0,
    support_staff: 0,
    total: 0
  };

  // Get staffing multiplier from national standards (default 1.0)
  const staffingFactor = standardConfig?.medical?.staffing_factor || 1.0;

  // =========================================================================
  // FALLBACK STAFFING RATIOS
  // Used only if constants lookup fails - primary source is MTF_CAPABILITIES_BY_ECHELON
  // These match the values in mtfCapabilities.js for safety
  // =========================================================================
  
  const fallbackStaffingRatios = {
    ROLE_1: {
      medical_officers: 2,
      nurses: 4,
      medics: 12,
      support_staff: 6,
      total_per_facility: 25
    },
    ROLE_2_BASIC: {
      medical_officers: 4,
      surgeons: 8,
      anesthesiologists: 4,
      nurses: 20,
      medics: 24,
      support_staff: 10,
      total_per_facility: 70
    },
    ROLE_2_ENHANCED: {
      medical_officers: 6,
      surgeons: 12,
      anesthesiologists: 6,
      nurses: 30,
      medics: 36,
      support_staff: 15,
      total_per_facility: 105
    },
    ROLE_3: {
      medical_officers: 8,
      surgeons: 16,
      anesthesiologists: 8,
      nurses: 60,
      medics: 40,
      support_staff: 68,
      total_per_facility: 250
    }
  };

  /**
   * Get staffing from constants or fallback
   * Uses getMTFCapabilities and getPersonnelBreakdown from mtfCapabilities.js
   * @param {string} roleLevel - ROLE_1, ROLE_2_BASIC, ROLE_2_ENHANCED, or ROLE_3
   * @returns {object} Staffing breakdown
   */
  const getStaffing = (roleLevel) => {
    try {
      // Get mapping for this role
      const mapping = ROLE_TO_ECHELON_MAP[roleLevel];
      if (!mapping) {
        console.warn(`No echelon mapping for role: ${roleLevel}, using fallback`);
        return fallbackStaffingRatios[roleLevel] || fallbackStaffingRatios.ROLE_1;
      }

      // Try using getMTFCapabilities helper function first
      if (typeof getMTFCapabilities === 'function') {
        const capabilities = getMTFCapabilities(mapping.echelon, mapping.roleKey);
        if (capabilities?.personnel_breakdown) {
          const breakdown = capabilities.personnel_breakdown;
          return {
            medical_officers: breakdown.medical_officers || 0,
            nurses: breakdown.nurses || 0,
            medics: breakdown.medics || 0,
            surgeons: breakdown.surgeons || 0,
            anesthesiologists: breakdown.anesthesiologists || 0,
            support_staff: (breakdown.support_staff || 0) + 
                           (breakdown.admin_staff || 0) + 
                           (breakdown.logistics || 0),
            total_per_facility: breakdown.total || capabilities.personnel || 0
          };
        }
      }

      // Try using getPersonnelBreakdown helper function
      if (typeof getPersonnelBreakdown === 'function') {
        const breakdown = getPersonnelBreakdown(mapping.echelon, mapping.roleKey);
        if (breakdown) {
          return {
            medical_officers: breakdown.medical_officers || 0,
            nurses: breakdown.nurses || 0,
            medics: breakdown.medics || 0,
            surgeons: breakdown.surgeons || 0,
            anesthesiologists: breakdown.anesthesiologists || 0,
            support_staff: (breakdown.support_staff || 0) + 
                           (breakdown.admin_staff || 0) + 
                           (breakdown.logistics || 0),
            total_per_facility: breakdown.total || 0
          };
        }
      }

      // Fallback to direct constant lookup
      const echelonCapabilities = MTF_CAPABILITIES_BY_ECHELON[mapping.echelon];
      if (!echelonCapabilities) {
        console.warn(`No capabilities for echelon: ${mapping.echelon}, using fallback`);
        return fallbackStaffingRatios[roleLevel] || fallbackStaffingRatios.ROLE_1;
      }

      // Get role-specific data
      const roleData = echelonCapabilities[mapping.roleKey];
      if (!roleData || !roleData.personnel_breakdown) {
        console.warn(`No personnel_breakdown for ${mapping.roleKey} in ${mapping.echelon}, using fallback`);
        return fallbackStaffingRatios[roleLevel] || fallbackStaffingRatios.ROLE_1;
      }

      // Map personnel_breakdown to our expected structure
      const breakdown = roleData.personnel_breakdown;
      return {
        medical_officers: breakdown.medical_officers || 0,
        nurses: breakdown.nurses || 0,
        medics: breakdown.medics || 0,
        surgeons: breakdown.surgeons || 0,
        anesthesiologists: breakdown.anesthesiologists || 0,
        support_staff: (breakdown.support_staff || 0) + 
                       (breakdown.admin_staff || 0) + 
                       (breakdown.logistics || 0),
        total_per_facility: breakdown.total || roleData.personnel || 0
      };
    } catch (error) {
      console.error(`Error getting staffing for ${roleLevel}:`, error);
      return fallbackStaffingRatios[roleLevel] || fallbackStaffingRatios.ROLE_1;
    }
  };

  // Role 1: Battalion Aid Station
  if (mtfStructure.role1?.count > 0) {
    const count = mtfStructure.role1.count;
    const staffing = getStaffing('ROLE_1');
    
    personnel.medical_officers += Math.round(count * (staffing.medical_officers || 0) * staffingFactor);
    personnel.nurses += Math.round(count * (staffing.nurses || 0) * staffingFactor);
    personnel.medics += Math.round(count * (staffing.medics || 0) * staffingFactor);
    personnel.support_staff += Math.round(count * (staffing.support_staff || 0) * staffingFactor);
  }

  // Role 2: Forward Surgical Team (Basic)
  if (mtfStructure.role2?.count > 0) {
    const count = mtfStructure.role2.count;
    const staffing = getStaffing('ROLE_2_BASIC');
    
    personnel.medical_officers += Math.round(count * (staffing.medical_officers || 0) * staffingFactor);
    personnel.surgeons += Math.round(count * (staffing.surgeons || 0) * staffingFactor);
    personnel.anesthesiologists += Math.round(count * (staffing.anesthesiologists || 0) * staffingFactor);
    personnel.nurses += Math.round(count * (staffing.nurses || 0) * staffingFactor);
    personnel.medics += Math.round(count * (staffing.medics || 0) * staffingFactor);
    personnel.support_staff += Math.round(count * (staffing.support_staff || 0) * staffingFactor);
  }

  // Role 2E: Enhanced FST
  if (mtfStructure.role2e?.count > 0) {
    const count = mtfStructure.role2e.count;
    const staffing = getStaffing('ROLE_2_ENHANCED');
    
    personnel.medical_officers += Math.round(count * (staffing.medical_officers || 0) * staffingFactor);
    personnel.surgeons += Math.round(count * (staffing.surgeons || 0) * staffingFactor);
    personnel.anesthesiologists += Math.round(count * (staffing.anesthesiologists || 0) * staffingFactor);
    personnel.nurses += Math.round(count * (staffing.nurses || 0) * staffingFactor);
    personnel.medics += Math.round(count * (staffing.medics || 0) * staffingFactor);
    personnel.support_staff += Math.round(count * (staffing.support_staff || 0) * staffingFactor);
  }

  // Role 3: Combat Support Hospital
  if (mtfStructure.role3?.count > 0) {
    const count = mtfStructure.role3.count;
    const staffing = getStaffing('ROLE_3');
    
    personnel.medical_officers += Math.round(count * (staffing.medical_officers || 0) * staffingFactor);
    personnel.surgeons += Math.round(count * (staffing.surgeons || 0) * staffingFactor);
    personnel.anesthesiologists += Math.round(count * (staffing.anesthesiologists || 0) * staffingFactor);
    personnel.nurses += Math.round(count * (staffing.nurses || 0) * staffingFactor);
    personnel.medics += Math.round(count * (staffing.medics || 0) * staffingFactor);
    personnel.support_staff += Math.round(count * (staffing.support_staff || 0) * staffingFactor);
  }

  personnel.total = 
    personnel.medical_officers +
    personnel.nurses +
    personnel.medics +
    personnel.surgeons +
    personnel.anesthesiologists +
    personnel.support_staff;

  return personnel;
}

// ============================================================================
// MEDICAL CASUALTY RISK MODELING
// Uses rates from constants
// ============================================================================

/**
 * Calculate medical personnel casualty risk and projections
 * Uses exposure rates that vary by role level
 * 
 * @param {object} scenario - Complete scenario object
 * @param {object} medicalPersonnel - From calculateMedicalPersonnel
 * @param {number} duration - Operation duration in days
 * @returns {object} Risk analysis and projections
 */
export function calculateMedicalCasualtyRisk(scenario, medicalPersonnel, duration) {
  // Get threat level modifier from scenario using constants
  const threatLevel = scenario?.operation_parameters?.threat_level || 'MODERATE';
  const threatMod = getThreatModifier(threatLevel);

  const riskModel = {
    by_role: {
      role_1_medics: {
        personnel_count: medicalPersonnel?.mtf_personnel?.medics || 0,
        exposure: 'HIGH',
        daily_casualty_rate: getMedicalExposureRate('HIGH') * threatMod,
        estimated_casualties: [],
        cumulative_casualties: 0,
        impact: 'Reduces CLS capability at point of injury'
      },
      role_2_staff: {
        personnel_count: (medicalPersonnel?.mtf_personnel?.nurses || 0) * 0.5 +
                        (medicalPersonnel?.mtf_personnel?.medical_officers || 0) * 0.5,
        exposure: 'MEDIUM',
        daily_casualty_rate: getMedicalExposureRate('MEDIUM') * threatMod,
        estimated_casualties: [],
        cumulative_casualties: 0,
        impact: 'Reduces surgical capacity'
      },
      role_3_staff: {
        personnel_count: (medicalPersonnel?.mtf_personnel?.nurses || 0) * 0.3,
        exposure: 'LOW',
        daily_casualty_rate: getMedicalExposureRate('LOW') * threatMod,
        estimated_casualties: [],
        cumulative_casualties: 0,
        impact: 'Minimal impact on hospital operations'
      },
      surgeons: {
        personnel_count: medicalPersonnel?.mtf_personnel?.surgeons || 0,
        exposure: 'LOW',
        daily_casualty_rate: getMedicalExposureRate('LOW') * threatMod,
        estimated_casualties: [],
        cumulative_casualties: 0,
        impact: 'CRITICAL - Each surgeon = 4-6 surgical beds',
        replacement_difficulty: 'EXTREME - 8-12 year training pipeline'
      },
      csu_pha_staff: {
        personnel_count: medicalPersonnel?.staging_facility_personnel?.total || 0,
        exposure: 'MEDIUM',
        daily_casualty_rate: getMedicalExposureRate('MEDIUM') * threatMod,
        estimated_casualties: [],
        cumulative_casualties: 0,
        impact: 'Reduces staging capacity'
      },
      hlz_staff: {
        personnel_count: medicalPersonnel?.hlz_personnel?.total || 0,
        exposure: 'MEDIUM',
        daily_casualty_rate: getMedicalExposureRate('MEDIUM') * threatMod,
        estimated_casualties: [],
        cumulative_casualties: 0,
        impact: 'Reduces helicopter operations capability'
      }
    },
    total_projected_casualties: 0,
    critical_losses: [],
    summary: {
      total_at_risk: medicalPersonnel?.total_medical_personnel || 0,
      expected_casualties: 0,
      casualty_rate_percent: 0,
      // Include reference to constants used
      threat_level_applied: threatLevel,
      threat_modifier_applied: threatMod,
      exposure_rates_reference: MEDICAL_EXPOSURE_RATES,
      threat_modifiers_reference: THREAT_LEVEL_MODIFIERS
    }
  };

  try {
    // Calculate projections for each role
    Object.keys(riskModel.by_role).forEach(roleKey => {
      const role = riskModel.by_role[roleKey];
      let cumulative = 0;

      for (let day = 1; day <= duration; day++) {
        const dailyCasualties = Math.round(role.personnel_count * role.daily_casualty_rate * 100) / 100;
        cumulative += dailyCasualties;
        
        role.estimated_casualties.push({
          day,
          daily: dailyCasualties,
          cumulative: Math.round(cumulative * 10) / 10
        });
      }

      role.cumulative_casualties = Math.round(cumulative);
      riskModel.total_projected_casualties += role.cumulative_casualties;

      // Flag critical losses
      if (roleKey === 'surgeons' && role.cumulative_casualties >= 1) {
        riskModel.critical_losses.push({
          role: 'Surgeons',
          projected_loss: role.cumulative_casualties,
          impact: 'CRITICAL - Each surgeon supports 4-6 beds, 10-15 surgeries/day',
          recommendation: 'Pre-position replacement surgeons or reduce surgical load'
        });
      }

      if (roleKey === 'role_1_medics' && role.cumulative_casualties >= role.personnel_count * 0.1) {
        riskModel.critical_losses.push({
          role: 'Forward Medics',
          projected_loss: role.cumulative_casualties,
          impact: 'HIGH - Reduces point-of-injury care capability',
          recommendation: 'Train additional combat lifesavers, request medic replacements'
        });
      }
    });

    // Summary
    riskModel.summary.expected_casualties = riskModel.total_projected_casualties;
    riskModel.summary.casualty_rate_percent = riskModel.summary.total_at_risk > 0 ?
      Math.round((riskModel.total_projected_casualties / riskModel.summary.total_at_risk) * 10000) / 100 : 0;

    return riskModel;
  } catch (error) {
    console.error('Error calculating medical casualty risk:', error);
    return riskModel;
  }
}

// ============================================================================
// RTD PROJECTION
// Uses calculateRTD from theatreHoldingPolicy.js
// ============================================================================

/**
 * Calculate Return to Duty projections for casualty cohort
 * Wrapper around constants function with scenario integration
 * 
 * @param {object} scenario - Complete scenario object
 * @param {object} casualties - Casualty breakdown by triage category
 * @returns {object} RTD projections
 */
export function calculateRTDProjection(scenario, casualties = null) {
  // Get THP from scenario
  const thpConfig = scenario?.logistics?.theatre_holding_policy;
  const thpDays = resolveInput(thpConfig?.policy_days || 
                  THEATRE_HOLDING_POLICY[thpConfig?.policy]?.days, 'thp_policy_days', { zeroIsMissing: true });

  // Get casualties from scenario or use provided
  let casualtyData = casualties || {
    t1: scenario?.casualty_projections?.by_triage?.t1 || 0,
    t2: scenario?.casualty_projections?.by_triage?.t2 || 0,
    t3: scenario?.casualty_projections?.by_triage?.t3 || 0,
    t4: scenario?.casualty_projections?.by_triage?.t4 || 0
  };

  // v2.3 FIX: Check if all casualties are 0 and generate fallback estimates
  const totalCasualties = casualtyData.t1 + casualtyData.t2 + casualtyData.t3 + casualtyData.t4;
  let isEstimated = false;
  let estimationNote = null;

  if (totalCasualties === 0) {
    // Get force strength and operation duration for fallback estimate
    const forceStrength = scenario?.force?.totalStrength || 
                          scenario?.force?.total_strength || 
                          scenario?.force?.forceBuilderResult?.corps?.strength || 0;
    const duration = resolveInput(scenario?.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });
    
    if (forceStrength > 0) {
      // Conservative historical rates:
      // - Battle casualties: 0.5% per day (moderate intensity)
      // - DNBI: 0.3% per day (disease, non-battle injury)
      const dailyBattleRate = 0.005;
      const dailyDNBIRate = 0.003;
      
      const totalBattleCasualties = Math.round(forceStrength * dailyBattleRate * duration);
      const totalDNBI = Math.round(forceStrength * dailyDNBIRate * duration);
      const estimatedTotal = totalBattleCasualties + totalDNBI;
      
      // Triage distribution (based on Ukraine 2022-2024 data)
      casualtyData = {
        t1: Math.round(estimatedTotal * 0.15),  // 15% immediate
        t2: Math.round(estimatedTotal * 0.25),  // 25% delayed
        t3: Math.round(estimatedTotal * 0.55),  // 55% minimal
        t4: Math.round(estimatedTotal * 0.05)   // 5% expectant
      };
      
      isEstimated = true;
      estimationNote = `Fallback estimate: ${estimatedTotal} total casualties based on ${forceStrength.toLocaleString()} personnel over ${duration} days (0.8% daily rate). Run Tab 5 for precise calculation.`;
    }
  }

  // Use constants function
  const rtdResult = constantsCalculateRTD(casualtyData, thpDays);

  // APP_RULES 6.1: Read evacuation priorities directly — throw if constants missing.
  // If getEvacuationPriority function is available use it; otherwise read from constant.
  // Either way: no silent string fallbacks.
  const _evacT1 = getEvacuationPriority ? getEvacuationPriority('T1') : EVACUATION_PRIORITIES?.T1;
  const _evacT2 = getEvacuationPriority ? getEvacuationPriority('T2') : EVACUATION_PRIORITIES?.T2;
  const _evacT3 = getEvacuationPriority ? getEvacuationPriority('T3') : EVACUATION_PRIORITIES?.T3;
  const _evacT4 = getEvacuationPriority ? getEvacuationPriority('T4') : EVACUATION_PRIORITIES?.T4;
  if (_evacT1 === undefined || _evacT2 === undefined || _evacT3 === undefined || _evacT4 === undefined) {
    throw new Error(
      `EVACUATION_PRIORITIES constants missing. ` +
      `Check theatreHoldingPolicy.js EVACUATION_PRIORITIES structure. ` +
      `T1=${_evacT1} T2=${_evacT2} T3=${_evacT3} T4=${_evacT4}`
    );
  }
  const evacuationPriorities = {
    t1: _evacT1,
    t2: _evacT2,
    t3: _evacT3,
    t4: _evacT4
  };

  // P1-04 FIX: Derive rtdTimeframe from thpDays to read correct RTD rates.
  // RTD_RATES.BY_TRIAGE_CATEGORY.[T].rtd_rates is keyed by timeframe string, not T1/T2/T3.
  let rtdTimeframe;
  if (thpDays <= 3) rtdTimeframe = 'within_72h';
  else if (thpDays <= 7) rtdTimeframe = 'within_7d';
  else if (thpDays <= 15) rtdTimeframe = 'within_15d';
  else rtdTimeframe = 'within_30d';

  // Calculate patient disposition using RTD_RATES and PATIENT_DISPOSITION
  // P1-04 FIX: Old code used RTD_RATES?.T1 which always returns undefined (wrong path).
  // Correct path: RTD_RATES.BY_TRIAGE_CATEGORY.T1_IMMEDIATE.rtd_rates[rtdTimeframe].
  // APP_RULES 6.1/6.3: No ?? fallbacks — throw if constants missing (same as casualtyCalculator.js v5.4.7).
  const _t1RtdRate = RTD_RATES?.BY_TRIAGE_CATEGORY?.T1_IMMEDIATE?.rtd_rates?.[rtdTimeframe];
  const _t2RtdRate = RTD_RATES?.BY_TRIAGE_CATEGORY?.T2_DELAYED?.rtd_rates?.[rtdTimeframe];
  const _t3RtdRate = RTD_RATES?.BY_TRIAGE_CATEGORY?.T3_MINIMAL?.rtd_rates?.[rtdTimeframe];
  const _t4RtdRate = RTD_RATES?.BY_TRIAGE_CATEGORY?.T4_EXPECTANT?.rtd_rates?.[rtdTimeframe];
  if (_t1RtdRate === undefined || _t2RtdRate === undefined || _t3RtdRate === undefined || _t4RtdRate === undefined) {
    throw new Error(
      `RTD_RATES constants missing for THP timeframe '${rtdTimeframe}'. ` +
      `Check theatreHoldingPolicy.js BY_TRIAGE_CATEGORY structure. ` +
      `T1=${_t1RtdRate} T2=${_t2RtdRate} T3=${_t3RtdRate} T4=${_t4RtdRate}`
    );
  }
  const t1RtdRate = _t1RtdRate;
  const t2RtdRate = _t2RtdRate;
  const t3RtdRate = _t3RtdRate;
  const t4RtdRate = _t4RtdRate;

  // APP_RULES 6.1: Read PATIENT_DISPOSITION codes directly — throw if constants missing.
  const _dispRTD   = PATIENT_DISPOSITION?.RTD?.code;
  const _dispHold  = PATIENT_DISPOSITION?.HOLD_THEATRE?.code;
  const _dispDied  = PATIENT_DISPOSITION?.DIED?.code;
  if (_dispRTD === undefined || _dispHold === undefined || _dispDied === undefined) {
    throw new Error(
      `PATIENT_DISPOSITION constants missing. ` +
      `Check theatreHoldingPolicy.js PATIENT_DISPOSITION structure. ` +
      `RTD=${_dispRTD} HOLD_THEATRE=${_dispHold} DIED=${_dispDied}`
    );
  }

  const patientDisposition = {
    rtd_by_triage: {
      t1: {
        count: casualtyData.t1,
        rtd_rate: t1RtdRate,
        expected_rtd: Math.round(casualtyData.t1 * t1RtdRate),
        disposition: _dispRTD
      },
      t2: {
        count: casualtyData.t2,
        rtd_rate: t2RtdRate,
        expected_rtd: Math.round(casualtyData.t2 * t2RtdRate),
        disposition: _dispHold
      },
      t3: {
        count: casualtyData.t3,
        rtd_rate: t3RtdRate,
        expected_rtd: Math.round(casualtyData.t3 * t3RtdRate),
        disposition: _dispHold
      },
      t4: {
        count: casualtyData.t4,
        rtd_rate: t4RtdRate,
        expected_rtd: Math.round(casualtyData.t4 * t4RtdRate),
        disposition: _dispDied
      }
    },
    // Include reference constants for UI/reporting
    evacuation_priorities_reference: EVACUATION_PRIORITIES,
    patient_disposition_reference: PATIENT_DISPOSITION,
    rtd_rates_reference: RTD_RATES
  };

  // Add scenario-specific context
  return {
    ...rtdResult,
    thp_policy: thpConfig?.policy || 'THP_7_DAY',
    thp_days: thpDays,
    evacuation_priorities: evacuationPriorities,
    patient_disposition: patientDisposition,
    strength_impact: {
      immediate_recovery: rtdResult.strength_recovery?.immediate || 0,
      week_recovery: rtdResult.strength_recovery?.within_week || 0,
      month_recovery: rtdResult.strength_recovery?.within_month || 0,
      permanent_loss: rtdResult.total_casualties - (rtdResult.strength_recovery?.within_month || 0)
    },
    // v2.3: Add estimation flag for UI display
    is_estimated: isEstimated,
    estimation_note: estimationNote
  };
}

// ============================================================================
// CSR CASUALTY PROJECTION
// Uses calculateCSRCasualties from mentalHealth.js
// ============================================================================

/**
 * Calculate Combat Stress Reaction casualties
 * Wrapper around constants function with scenario integration
 * 
 * @param {object} scenario - Complete scenario object
 * @returns {object} CSR projections
 */
export function calculateCSRProjection(scenario) {
  // v2.2 FIX: Tab1 saves as 'totalStrength' (camelCase), not 'total_strength' (snake_case)
  const forceStrength = resolveInput(scenario?.force?.totalStrength || 
                        scenario?.force?.total_strength || 
                        scenario?.force?.forceBuilderResult?.corps?.strength, 'force_total_strength', { zeroIsMissing: true });
  const duration = resolveInput(scenario?.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });
  const intensity = scenario?.operation_parameters?.combat_intensity || 'MODERATE_INTENSITY';
  
  // Get modifier factors for risk/protective arrays
  const threatLevel = scenario?.operation_parameters?.threat_level || 'MODERATE';
  
  // Build risk factors array based on scenario
  const riskFactors = [];
  if (threatLevel === 'HIGH' || threatLevel === 'SEVERE') {
    riskFactors.push('HIGH_THREAT');
  }
  if (scenario?.force?.previous_combat) {
    riskFactors.push('PREVIOUS_COMBAT');
  }
  if (scenario?.force?.unit_cohesion === 'LOW') {
    riskFactors.push('LOW_COHESION');
  }
  
  // Build protective factors array based on scenario
  const protectiveFactors = [];
  if (scenario?.force?.unit_cohesion === 'HIGH') {
    protectiveFactors.push('HIGH_COHESION');
  }
  if (scenario?.force?.leadership_quality === 'EXCELLENT') {
    protectiveFactors.push('GOOD_LEADERSHIP');
  }
  if (scenario?.logistics?.mental_health?.csc_deployed) {
    protectiveFactors.push('CSC_DEPLOYED');
  }
  
  // 2026-10-10: one source for combat stress. When the casualty engine has run (Tab 3 save), the projection IS the
  // engine's result (wounded-anchored volume, Tab 3 psychiatric support), summed over all brigades and days.
  // The per-1,000 intensity rates below are only the preliminary estimate before the engine has run, and are labelled so.
  const engineBrigades = scenario?.casualty_projections?.by_brigade;
  const engineDays = Array.isArray(engineBrigades)
    ? engineBrigades.flatMap(b => (b.by_day || []).map(d => d.dnbi).filter(d => d && Number.isFinite(d.csr)))
    : [];
  const csrFromEngine = engineDays.length > 0 ? (() => {
    const total = engineDays.reduce((sum, d) => sum + d.csr, 0);
    const rtd = engineDays.reduce((sum, d) => sum + requireNumber(d.csr_rtd, 'personnelIntelligence.calculateCSRProjection', 'by_day.dnbi.csr_rtd'), 0);
    return {
      force_strength: forceStrength,
      duration_days: duration,
      total_csr_casualties: total,
      daily_csr_rate: Math.round((total / (forceStrength * duration)) * 10000) / 10000,
      rtd_within_72h: rtd,
      require_evacuation: total - rtd,
      source: 'ENGINE'
    };
  })() : null;

  // v2.2 FIX: Call with correct signature (positional params, not object)
  // calculateCSRCasualties(forceStrength, intensity, days, riskFactors, protectiveFactors)
  const csrResult = csrFromEngine || { ...calculateCSRCasualties(
    forceStrength,
    intensity,
    duration,
    riskFactors,
    protectiveFactors
  ), source: 'PRELIMINARY_RATES' };

  // Get mental health support requirements
  const supportReq = calculateMentalHealthSupport(forceStrength, intensity);

  // Calculate CSR breakdown using CSR_RATES constant
  const csrBreakdown = {
    by_intensity: {
      current_intensity: intensity,
      base_rate: CSR_RATES?.[intensity] || CSR_RATES?.MODERATE_INTENSITY || 0.02,
      adjusted_rate: csrResult.daily_csr_rate || (CSR_RATES?.[intensity] || 0.02),
      intensity_rates_reference: CSR_RATES
    },
    // Mental health support standards from constants
    mental_health_support_standards: MENTAL_HEALTH_SUPPORT || {
      restoration_beds_per_1000: 5,
      psychiatrist_per_division: 1,
      psychologist_per_brigade: 2,
      csc_team_composition: {
        psychiatrists: 1,
        psychologists: 2,
        social_workers: 3,
        chaplains: 2
      }
    },
    // Calculate if current staffing meets standards
    staffing_adequacy: {
      // Restoration beds follow the days a casualty needs treatment: psychiatric support (Tab 3) shortens it
      // (constants/psychiatricSupport.js treatmentDaysMultiplier; STANDARD = 1.0 = the reference ratio).
      required_restoration_beds: Math.ceil(forceStrength / 1000 * requireNumber(MENTAL_HEALTH_SUPPORT?.BEDS?.RESTORATION?.ratio_per_1000, 'personnelIntelligence', 'MENTAL_HEALTH_SUPPORT.BEDS.RESTORATION.ratio_per_1000') *
        PSYCHIATRIC_SUPPORT_LEVELS[resolvePsychiatricSupport(scenario?.medical_configuration).level].treatmentDaysMultiplier),
      psychiatric_support_level: resolvePsychiatricSupport(scenario?.medical_configuration).level,
      required_mh_personnel: Math.ceil(forceStrength / 5000 * requireNumber(MENTAL_HEALTH_SUPPORT?.PLANNING?.mh_personnel_per_5000_troops, 'personnelIntelligence', 'MENTAL_HEALTH_SUPPORT.PLANNING.mh_personnel_per_5000_troops')),
      current_restoration_beds: scenario?.logistics?.mental_health?.restoration_beds || 0,
      current_mh_personnel: (scenario?.logistics?.mental_health?.psychiatrists || 0) +
                            (scenario?.logistics?.mental_health?.psychologists || 0) +
                            (scenario?.logistics?.mental_health?.social_workers || 0)
    }
  };

  return {
    ...csrResult,
    support_requirements: supportReq,
    biceps_principles: BICEPS_PRINCIPLES,
    csr_breakdown: csrBreakdown,
    recommendations: generateCSRRecommendations(csrResult, scenario)
  };
}

/**
 * Generate CSR-specific recommendations
 * Uses CSR_THRESHOLDS from constants
 */
function generateCSRRecommendations(csrResult, scenario) {
  const recommendations = [];
  
  const mhStaff = scenario?.logistics?.mental_health;
  const hasCSCTeam = mhStaff?.csc_team_deployed || false;
  
  // Use threshold from constants
  if (csrResult.total_csr_casualties > CSR_THRESHOLDS.csc_team_deployment_threshold && !hasCSCTeam) {
    recommendations.push({
      priority: 'HIGH',
      message: `${csrResult.total_csr_casualties} CSR casualties projected - CSC team recommended`,
      action: 'Deploy Combat Stress Control team'
    });
  }

  // Use threshold from constants
  if (csrResult.daily_csr_rate > CSR_THRESHOLDS.high_daily_rate_threshold) {
    recommendations.push({
      priority: 'MEDIUM',
      message: 'High daily CSR rate expected',
      action: 'Ensure restoration beds available, train peer support'
    });
  }

  return recommendations;
}

// ============================================================================
// REPLACEMENT PLANNING
// Uses REPLACEMENT_PLANNING constants
// ============================================================================

/**
 * Generate replacement recommendations based on guided questions
 * @param {object} scenario - Complete scenario object
 * @param {object} guidedAnswers - Answers from guided replacement wizard
 * @returns {array} Replacement recommendations
 */
export function generateReplacementRecommendations(scenario, guidedAnswers = {}) {
  const recommendations = [];
  
  if (!scenario || !guidedAnswers) {
    return recommendations;
  }

  try {
    const duration = resolveInput(scenario.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });
    const totalCasualties = scenario.casualty_projections?.total || 0;
    
    // 1. Initial request timing recommendation
    const arrivalTime = guidedAnswers.replacement_arrival_time || '5-7 days from request';
    const leadDays = parseArrivalTime(arrivalTime);
    
    // When should first request be submitted?
    const medicalPersonnel = calculateMedicalPersonnel(scenario);
    const medCasualtyRisk = calculateMedicalCasualtyRisk(scenario, medicalPersonnel, duration);
    
    // Find first day with significant casualties (using constant)
    const firstCasualtiesDay = Math.ceil(duration * REPLACEMENT_PLANNING.casualty_onset_fraction);
    const requestDay = Math.max(1, firstCasualtiesDay - leadDays);
    
    recommendations.push({
      category: 'TIMING',
      priority: 'HIGH',
      recommendation: `Submit initial replacement request on Day ${requestDay}`,
      rationale: `${leadDays}-day replacement pipeline requires advance planning`,
      details: [
        `Request submitted: Day ${requestDay}`,
        `Replacements arrive: Day ${firstCasualtiesDay}`,
        'Timing ensures replacements available when needed'
      ]
    });

    // 2. Combat vs Medical priority recommendation
    const priority = guidedAnswers.priority_combat_vs_medical || 'balanced';
    
    if (priority === 'medical_first') {
      recommendations.push({
        category: 'PRIORITY',
        priority: 'HIGH',
        recommendation: 'Prioritize medical personnel replacements',
        rationale: 'Medical force multiplier effect - each medical loss affects multiple casualties',
        details: [
          'Replace surgeons within 7 days (each surgeon supports 4-6 beds)',
          'Replace combat medics within 3 days (affects point of injury care)',
          'Combat personnel can be backfilled more flexibly'
        ]
      });
    } else if (priority === 'combat_first') {
      recommendations.push({
        category: 'PRIORITY',
        priority: 'MEDIUM',
        recommendation: 'Prioritize combat force strength',
        rationale: 'Maintain operational capability - fewer casualties if force strength high',
        details: [
          'Replace combat losses to maintain unit effectiveness',
          'Medical personnel losses can be partially mitigated by workload adjustment',
          'Risk: Medical system degradation if sustained casualties'
        ]
      });
    } else {
      recommendations.push({
        category: 'PRIORITY',
        priority: 'MEDIUM',
        recommendation: 'Balanced replacement approach',
        rationale: 'Maintain both combat effectiveness and medical capability',
        details: [
          'Replace critical medical personnel (surgeons) immediately',
          `Replace combat force to maintain ${Math.round(REPLACEMENT_PLANNING.minimum_strength_threshold * 100)}%+ strength`,
          'Adjust based on actual casualty patterns'
        ]
      });
    }

    // 3. Replacement wave sizing (using constants)
    const projectedCasualtyRate = totalCasualties / duration;
    const waveSize = Math.ceil(projectedCasualtyRate * REPLACEMENT_PLANNING.wave_configuration.days_per_wave_coverage);
    
    const waveInterval = REPLACEMENT_PLANNING.wave_configuration.wave_interval_days;
    const maxWaves = REPLACEMENT_PLANNING.wave_configuration.max_waves;
    const waveCount = Math.ceil(duration / waveInterval);
    
    for (let wave = 1; wave <= waveCount && wave <= maxWaves; wave++) {
      const waveDay = wave * waveInterval;
      if (waveDay <= duration) {
        const combatReplacements = Math.ceil(waveSize * PERSONNEL_CASUALTY_DISTRIBUTION.combat_personnel_ratio);
        const medicalReplacements = Math.ceil(waveSize * PERSONNEL_CASUALTY_DISTRIBUTION.medical_personnel_ratio);
        const transportVehicles = Math.ceil(waveSize / REPLACEMENT_PLANNING.wave_configuration.transport_capacity_per_vehicle);
        
        recommendations.push({
          category: 'WAVE_SCHEDULE',
          priority: 'MEDIUM',
          recommendation: `Replacement Wave ${wave}: Day ${waveDay}`,
          rationale: `Maintain force strength above ${Math.round(REPLACEMENT_PLANNING.minimum_strength_threshold * 100)}% threshold`,
          details: [
            `Combat personnel: ${combatReplacements} replacements`,
            `Medical personnel: ${medicalReplacements} replacements`,
            `Transport requirement: ${transportVehicles} vehicles`
          ]
        });
      }
    }

    // 4. Critical specialty recommendations
    if (medCasualtyRisk.critical_losses.length > 0) {
      medCasualtyRisk.critical_losses.forEach(loss => {
        recommendations.push({
          category: 'CRITICAL_SPECIALTY',
          priority: 'HIGH',
          recommendation: `Urgent ${loss.role} replacement needed`,
          rationale: loss.impact,
          details: [
            `Projected loss: ${loss.projected_loss} personnel`,
            loss.recommendation
          ]
        });
      });
    }

    return recommendations;
  } catch (error) {
    console.error('Error generating replacement recommendations:', error);
    return recommendations;
  }
}

// ============================================================================
// RESERVE POOL MANAGEMENT
// ============================================================================

/**
 * Calculate reserve pool depletion over time
 * @param {object} scenario - Complete scenario object
 * @param {object} casualtyProjection - Daily casualties
 * @param {object} replacementSchedule - Planned replacements
 * @returns {object} Reserve pool status over time
 */
/**
 * Calculate Reserve Pool Depletion
 * Projects how long reserve pools will last given casualty rates
 * 
 * v2.4 UPDATE (January 13, 2025):
 * - Now incorporates personnel_arrivals schedule from Tab 4
 * - Arrivals add to reserves on their scheduled day by type (COMBAT/MEDICAL)
 * 
 * @param {object} scenario - Complete scenario object
 * @param {object} casualtyProjection - Casualty projection data
 * @param {object} replacementSchedule - Legacy replacement schedule (manual_schedule)
 * @returns {object} Depletion analysis with daily status and warnings
 */
export function calculateReservePoolDepletion(scenario, casualtyProjection, replacementSchedule) {
  const duration = resolveInput(scenario.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });
  const initialPool = scenario.personnel?.replacement_planning?.reserve_pool || {
    combat_reserves: 0,
    medical_reserves: 0
  };

  // v2.4: Get personnel arrivals schedule from logistics
  const personnelArrivals = scenario.logistics?.personnel_arrivals || [];

  const depletion = {
    daily_status: [],
    warning_days: [],
    critical_days: [],
    final_status: { ...initialPool },
    summary: {
      days_until_depletion_combat: null,
      days_until_depletion_medical: null,
      replenishment_needed: false,
      total_arrivals: personnelArrivals.reduce((sum, a) => sum + (a.quantity || 0), 0)
    }
  };

  try {
    let currentPool = { ...initialPool };
    const schedule = replacementSchedule?.manual_schedule || [];

    for (let day = 1; day <= duration; day++) {
      // Subtract casualties using distribution from constants
      const dailyCasualties = casualtyProjection?.daily?.[day - 1] || 
                              casualtyProjection?.by_day?.[day - 1]?.total ||
                              0;
      const combatCasualties = Math.ceil(dailyCasualties * PERSONNEL_CASUALTY_DISTRIBUTION.combat_personnel_ratio);
      const medicalCasualties = Math.floor(dailyCasualties * PERSONNEL_CASUALTY_DISTRIBUTION.medical_personnel_ratio);

      currentPool.combat_reserves = Math.max(0, currentPool.combat_reserves - combatCasualties);
      currentPool.medical_reserves = Math.max(0, currentPool.medical_reserves - medicalCasualties);

      // Add scheduled replacements (legacy format)
      const dayReplacement = schedule.find(r => r.day === day);
      if (dayReplacement) {
        currentPool.combat_reserves += Math.ceil(dayReplacement.personnel_count * PERSONNEL_CASUALTY_DISTRIBUTION.combat_personnel_ratio);
        currentPool.medical_reserves += Math.floor(dayReplacement.personnel_count * PERSONNEL_CASUALTY_DISTRIBUTION.medical_personnel_ratio);
      }

      // v2.4: Add personnel arrivals from Tab 4 schedule
      const dayArrivals = personnelArrivals.filter(a => a.day === day);
      for (const arrival of dayArrivals) {
        if (arrival.type === 'COMBAT') {
          currentPool.combat_reserves += arrival.quantity || 0;
        } else if (arrival.type === 'MEDICAL') {
          currentPool.medical_reserves += arrival.quantity || 0;
        }
      }

      // Store daily status
      const dayStatus = {
        day,
        combat_reserves: currentPool.combat_reserves,
        medical_reserves: currentPool.medical_reserves,
        combat_percentage: initialPool.combat_reserves > 0 
          ? Math.round((currentPool.combat_reserves / initialPool.combat_reserves) * 100)
          : 0,
        medical_percentage: initialPool.medical_reserves > 0
          ? Math.round((currentPool.medical_reserves / initialPool.medical_reserves) * 100)
          : 0,
        arrivals_today: dayArrivals.reduce((sum, a) => sum + (a.quantity || 0), 0)  // v2.4
      };
      
      depletion.daily_status.push(dayStatus);

      // Check for warnings and track depletion days
      if (currentPool.combat_reserves <= 0 && depletion.summary.days_until_depletion_combat === null) {
        depletion.summary.days_until_depletion_combat = day;
      }
      if (currentPool.medical_reserves <= 0 && depletion.summary.days_until_depletion_medical === null) {
        depletion.summary.days_until_depletion_medical = day;
      }

      // Track warning/critical days
      if (dayStatus.combat_percentage < 20 || dayStatus.medical_percentage < 20) {
        depletion.critical_days.push(day);
      } else if (dayStatus.combat_percentage < 40 || dayStatus.medical_percentage < 40) {
        depletion.warning_days.push(day);
      }
    }

    depletion.final_status = currentPool;
    depletion.summary.replenishment_needed = 
      depletion.summary.days_until_depletion_combat !== null ||
      depletion.summary.days_until_depletion_medical !== null;

    return depletion;
  } catch (error) {
    console.error('Error calculating reserve pool depletion:', error);
    return depletion;
  }
}

// ============================================================================
// BED OCCUPANCY CALCULATION
// Uses calculateBedOccupancy from theatreHoldingPolicy.js
// v2.5 FIX: Now properly finds facilities from multiple data paths
// ============================================================================

/**
 * Calculate bed occupancy based on THP and casualty flow
 * Wrapper around constants function
 * 
 * v2.5 FIX: Checks multiple data paths for facilities:
 * 1. medical_configuration.facilities (Tab 3 primary)
 * 2. medical_configuration.mtf_list
 * 3. force.forceBuilderResult.facilities
 * 4. medical_configuration.mtf_structure (legacy)
 * 5. Fallback based on force echelon
 * 
 * @param {object} scenario - Complete scenario object
 * @returns {object} Bed occupancy analysis
 */
export function calculateBedOccupancyAnalysis(scenario) {
  let totalBeds = 0;
  let bedSource = 'none';
  
  // ==========================================================================
  // v2.5 FIX: Check multiple data paths for facilities
  // ==========================================================================
  
  // Priority 1: medical_configuration.facilities (Tab 3 primary save path)
  // v2.11 FIX (BED-D-004): Exclude Role 1. Tab3 saves ALL forceBuilderFacilities including Role 1
  // treatment positions (6 stretchers per BAS). Role 1 is non-holding per AJMedP-1 — these are
  // throughput positions, not inpatient beds. 9 brigades × ~3 Role1 × 6 beds = ~162 beds wrongly
  // inflating the total, masking the true R2B/R2E shortage. Exclude everywhere (P1/P2/P3).
  const isRole1Facility = (f) => ['ROLE1', 'ROLE_1'].includes(f.type || f.role || f.role_type || '');
  const getBedCount = (f) => f.beds || f.bed_count || f.capacity || 0;
  const facilities = scenario?.medical_configuration?.facilities;
  if (Array.isArray(facilities) && facilities.length > 0) {
    bedSource = 'medical_configuration.facilities';
    facilities.forEach(f => {
      if (!isRole1Facility(f)) totalBeds += getBedCount(f);
    });
  }
  
  // Priority 2: medical_configuration.mtf_list
  if (totalBeds === 0) {
    const mtfList = scenario?.medical_configuration?.mtf_list;
    if (Array.isArray(mtfList) && mtfList.length > 0) {
      bedSource = 'medical_configuration.mtf_list';
      mtfList.forEach(m => {
        if (!isRole1Facility(m)) totalBeds += getBedCount(m);
      });
    }
  }
  
  // Priority 3: force.forceBuilderResult.facilities
  if (totalBeds === 0) {
    const fbFacilities = scenario?.force?.forceBuilderResult?.facilities;
    if (Array.isArray(fbFacilities) && fbFacilities.length > 0) {
      bedSource = 'force.forceBuilderResult.facilities';
      fbFacilities.forEach(f => {
        if (!isRole1Facility(f)) totalBeds += getBedCount(f);
      });
    }
  }
  
  // Priority 4: Legacy mtf_structure format
  if (totalBeds === 0) {
    const mtf = scenario?.medical_configuration?.mtf_structure;
    if (mtf) {
      bedSource = 'medical_configuration.mtf_structure';
      // v2.8 FIX (APP_RULES 6.1): Role 1 fallback was || 10 — wrong value (doctrine = 6 stretcher
      // treatment positions, non-holding per AJMedP-1) and a silent fallback masking missing data.
      // Changed to || 0: Role 1 is non-holding; if beds field absent, it contributes nothing.
      // Role 2 Basic fallback corrected from || 50 to || 30 (ROLE_2_BASIC_DEFINITION.holding_capacity.beds).
      totalBeds += (mtf.role1?.beds || 0) * (mtf.role1?.count || 0);
      totalBeds += (mtf.role2?.beds || 30) * (mtf.role2?.count || 0);
      totalBeds += (mtf.role2e?.beds || 100) * (mtf.role2e?.count || 0);
      totalBeds += (mtf.role3?.beds || 200) * (mtf.role3?.count || 0);
    }
  }
  
  // Priority 5: Fallback based on force echelon
  // Estimate beds based on typical MTF allocation per echelon
  if (totalBeds === 0) {
    bedSource = 'fallback_echelon_estimate';
    const echelon = scenario?.force?.echelon || 'BRIGADE';
    const forceStrength = resolveInput(scenario?.force?.totalStrength || 
                          scenario?.force?.total_strength || 
                          scenario?.force?.forceBuilderResult?.corps?.strength, 'force_total_strength', { zeroIsMissing: true });
    
    // Typical bed allocation: ~2-3% of force strength
    // Corps (45,000): ~1,200 beds
    // Division (15,000): ~400 beds  
    // Brigade (4,500): ~120 beds
    const ECHELON_BED_MULTIPLIERS = {
      CORPS: 0.027,      // ~1,200 beds for 45,000 troops
      DIVISION: 0.027,   // ~400 beds for 15,000 troops
      BRIGADE: 0.027,    // ~120 beds for 4,500 troops
      BATTALION: 0.02    // ~20 beds for 1,000 troops
    };
    
    const multiplier = ECHELON_BED_MULTIPLIERS[echelon] || 0.025;
    totalBeds = Math.round(forceStrength * multiplier);
  }
  
  // Ensure minimum beds to prevent division by zero
  if (totalBeds < 10) {
    totalBeds = 100; // Minimum realistic bed count
    bedSource += ' (minimum applied)';
    console.warn('⚠️ Bed Occupancy: totalBeds too low, using minimum of 100');
  }

  // ==========================================================================
  // v2.13 BED-D-005: Capture baseBedSource BEFORE CSU/PHA append.
  // The per-role dispatch uses strict === equality. If bedSource is mutated
  // by the CSU/PHA block below (e.g. '...facilities + 90 CSU/PHA beds'),
  // every per-role branch misses and all three role cards show "No beds configured".
  // baseBedSource holds the clean path string for per-role dispatch only.
  // bedSource continues to be used for display (includes the CSU/PHA annotation).
  // ==========================================================================
  const baseBedSource = bedSource;

  // ==========================================================================
  // v2.12 CSU-PHA-BED: Add Casualty Staging Unit + Patient Holding Area beds
  // when advanced staging is enabled (Tab 3 Section 3G).
  // CSU and PHA beds are forward holding capacity that extends the R2B/R2E
  // bed pool — they must be counted in total_beds for accurate occupancy %.
  // Source: medical_configuration.staging_facilities[].capacity_patients
  //   where facility_type === 'CSU' or 'PHA'.
  // Gate: only when use_advanced_staging === true (user explicitly enabled).
  // Consistent with casualtyCalculator.js v5.9.0 CSU-BED fix (R2B MASCAL timeline).
  // ==========================================================================
  let csuPhaBeds = 0;
  if (scenario?.medical_configuration?.use_advanced_staging === true) {
    const stagingFacilities = scenario?.medical_configuration?.staging_facilities;
    if (Array.isArray(stagingFacilities)) {
      stagingFacilities.forEach(sf => {
        if (sf.facility_type === 'CSU' || sf.facility_type === 'PHA') {
          csuPhaBeds += sf.capacity_patients || 0;
        }
      });
      if (csuPhaBeds > 0) {
        totalBeds += csuPhaBeds;
        bedSource += ` + ${csuPhaBeds} CSU/PHA beds`;
      }
    }
  }

  // ==========================================================================
  // Get THP days
  // ==========================================================================
  const thpConfig = scenario?.logistics?.theatre_holding_policy;
  let thpDays = 7; // Default
  
  if (thpConfig?.policy) {
    // Extract days from policy name like "THP_7_DAY" → 7
    const match = thpConfig.policy.match(/THP_(\d+)_DAY/);
    if (match) {
      thpDays = parseInt(match[1], 10);
    }
  }
  if (thpConfig?.policy_days) {
    thpDays = thpConfig.policy_days;
  }

  // ==========================================================================
  // Get daily admissions (average)
  // ==========================================================================
  const duration = resolveInput(scenario?.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });
  const totalCasualties = scenario?.casualty_projections?.total || 
                          scenario?.calculation_results?.summary?.total_casualties || 0;
  
  // Not all casualties need beds - only ~30-40% (T1/T2 requiring hospitalization)
  // T3 (routine) and DNBI (minor) typically don't occupy beds.
  // v2.9 (APP_RULES 2.1): HOSPITALIZATION_RATE from theatreHoldingPolicy.js (was inline 0.35).
  const dailyCasualties = totalCasualties / duration;
  const dailyAdmissions = dailyCasualties * HOSPITALIZATION_RATE;

  // ==========================================================================
  // Per-role daily admissions
  // v2.9: ECHELON_ADMISSION_DISTRIBUTION from theatreHoldingPolicy.js.
  // Each role receives a fraction of total admitted casualties based on
  // AJMedP-1 evacuation chain patient distribution (R2B=60%, R2E=30%, R3=10%).
  // Role 1 is excluded — non-holding per AJMedP-1 (max 4-hour throughput).
  // ==========================================================================
  const role2bAdmissions = dailyAdmissions * ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_BASIC;
  const role2eAdmissions = dailyAdmissions * ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_ENHANCED;
  const role3Admissions  = dailyAdmissions * ECHELON_ADMISSION_DISTRIBUTION.ROLE_3;

  // ==========================================================================
  // System-wide calculation (blended — all roles combined)
  // ==========================================================================
  const result = calculateBedOccupancy(dailyAdmissions, thpDays, totalBeds);

  // ==========================================================================
  // Per-role bed counts — must use the SAME data path as system-wide totalBeds.
  // v2.10 FIX (BED-D-001/002/003): v2.9 always read mtf_structure regardless of bedSource.
  // v2.11: Role 1 excluded here too (non-holding per AJMedP-1).
  // ==========================================================================
  let role2bBeds = 0;
  let role2eBeds = 0;
  let role3Beds  = 0;

  const isRole2B = (f) => ['ROLE2_BASIC','ROLE_2_BASIC','ROLE2B','Role2Basic','role2_basic'].includes(f.role || f.type || f.role_type || f.mtf_role || '');
  const isRole2E = (f) => ['ROLE2_ENHANCED','ROLE_2_ENHANCED','ROLE2E','Role2Enhanced','role2_enhanced'].includes(f.role || f.type || f.role_type || f.mtf_role || '');
  const isRole3  = (f) => ['ROLE3','ROLE_3','Role3','role3'].includes(f.role || f.type || f.role_type || f.mtf_role || '');

  if (baseBedSource === 'medical_configuration.facilities') {
    const fac = scenario?.medical_configuration?.facilities || [];
    fac.forEach(f => {
      if (isRole2B(f))      role2bBeds += getBedCount(f);
      else if (isRole2E(f)) role2eBeds += getBedCount(f);
      else if (isRole3(f))  role3Beds  += getBedCount(f);
    });
  } else if (baseBedSource === 'medical_configuration.mtf_list') {
    const list = scenario?.medical_configuration?.mtf_list || [];
    list.forEach(f => {
      if (isRole2B(f))      role2bBeds += getBedCount(f);
      else if (isRole2E(f)) role2eBeds += getBedCount(f);
      else if (isRole3(f))  role3Beds  += getBedCount(f);
    });
  } else if (baseBedSource === 'force.forceBuilderResult.facilities') {
    const fbFac = scenario?.force?.forceBuilderResult?.facilities || [];
    fbFac.forEach(f => {
      if (isRole2B(f))      role2bBeds += getBedCount(f);
      else if (isRole2E(f)) role2eBeds += getBedCount(f);
      else if (isRole3(f))  role3Beds  += getBedCount(f);
    });
  } else if (baseBedSource === 'medical_configuration.mtf_structure') {
    const mtfStr = scenario?.medical_configuration?.mtf_structure;
    if (mtfStr) {
      role2bBeds = (mtfStr.role2?.beds  || 30)  * (mtfStr.role2?.count  || 0);
      role2eBeds = (mtfStr.role2e?.beds || 100) * (mtfStr.role2e?.count || 0);
      role3Beds  = (mtfStr.role3?.beds  || 200) * (mtfStr.role3?.count  || 0);
    }
  }
  // fallback_echelon_estimate: no per-role breakdown possible — beds remain 0 → null entries

  const role2bOccupancy = role2bBeds > 0
    ? calculateBedOccupancy(role2bAdmissions, thpDays, role2bBeds)
    : null;
  const role2eOccupancy = role2eBeds > 0
    ? calculateBedOccupancy(role2eAdmissions, thpDays, role2eBeds)
    : null;
  const role3Occupancy = role3Beds > 0
    ? calculateBedOccupancy(role3Admissions, thpDays, role3Beds)
    : null;

  // Add metadata to result
  return {
    ...result,
    total_beds: totalBeds,
    csu_pha_beds: csuPhaBeds,  // v2.12: CSU + PHA staging beds included in total (0 if disabled)
    bed_source: bedSource,
    hospitalization_rate: HOSPITALIZATION_RATE,
    daily_casualties: Math.round(dailyCasualties * 10) / 10,
    // Per-role breakdown (null when bed counts not available from scenario)
    per_role: {
      role_2_basic:    role2bOccupancy ? { ...role2bOccupancy, beds: role2bBeds, daily_admissions: Math.round(role2bAdmissions * 10) / 10 } : null,
      role_2_enhanced: role2eOccupancy ? { ...role2eOccupancy, beds: role2eBeds, daily_admissions: Math.round(role2eAdmissions * 10) / 10 } : null,
      role_3:          role3Occupancy  ? { ...role3Occupancy,  beds: role3Beds,  daily_admissions: Math.round(role3Admissions  * 10) / 10 } : null,
    }
  };
}

// ============================================================================
// VALIDATION
// ============================================================================

// ============================================================================
// T45-004: PERSONNEL ADEQUACY CHECK
// ============================================================================

/**
 * Calculate Personnel Adequacy
 * Compares available personnel against requirements based on MTF configuration
 * 
 * @param {object} scenario - Complete scenario object
 * @returns {object} Personnel adequacy analysis
 */
export function calculatePersonnelAdequacy(scenario) {
  // ============================================================================
  // BUG-082 FIX (v2.14): calculatePersonnelAdequacy was reading only
  // medical_configuration.mtf_list, which Tab 3 does NOT populate — Tab 3 saves
  // to medical_configuration.facilities (Priority 1). mtf_list is always []
  // so mtfCounts were always 0 → required = 0 → percent formula returns 100
  // for every category → dashboard always showed 100% ADEQUATE regardless of
  // actual staffing. Fix: mirror the same 4-path facility resolution used by
  // calculateBedOccupancyAnalysis. Role-type matchers expanded to check
  // role_type and mtf_role fields (consistent with rest of file).
  // ============================================================================

  // Resolve facility list from the same 4 paths as calculateBedOccupancyAnalysis
  const getField = (f) => f.role || f.type || f.role_type || f.mtf_role || '';

  let facilityList = [];
  let facilitySource = 'none';

  // Priority 1: medical_configuration.facilities (Tab 3 primary save path)
  const facP1 = scenario?.medical_configuration?.facilities;
  if (Array.isArray(facP1) && facP1.length > 0) {
    facilityList = facP1;
    facilitySource = 'medical_configuration.facilities';
  }

  // Priority 2: medical_configuration.mtf_list
  if (facilityList.length === 0) {
    const facP2 = scenario?.medical_configuration?.mtf_list;
    if (Array.isArray(facP2) && facP2.length > 0) {
      facilityList = facP2;
      facilitySource = 'medical_configuration.mtf_list';
    }
  }

  // Priority 3: force.forceBuilderResult.facilities
  if (facilityList.length === 0) {
    const facP3 = scenario?.force?.forceBuilderResult?.facilities;
    if (Array.isArray(facP3) && facP3.length > 0) {
      facilityList = facP3;
      facilitySource = 'force.forceBuilderResult.facilities';
    }
  }

  // Priority 4: legacy mtf_structure — flatten to array for uniform counting
  if (facilityList.length === 0) {
    const mtfStruct = scenario?.medical_configuration?.mtf_structure;
    if (mtfStruct) {
      facilitySource = 'medical_configuration.mtf_structure';
      // Expand counts into synthetic facility objects so the filter below works uniformly
      const expand = (roleKey, roleType, count) => {
        const n = count || 0;
        return Array.from({ length: n }, () => ({ role: roleType }));
      };
      facilityList = [
        ...expand('role1',  'ROLE_1',         mtfStruct.role1?.count  || 0),
        ...expand('role2b', 'ROLE_2_BASIC',   mtfStruct.role2?.count  || 0),
        ...expand('role2e', 'ROLE_2_ENHANCED',mtfStruct.role2e?.count || 0),
        ...expand('role3',  'ROLE_3',         mtfStruct.role3?.count  || 0),
      ];
    }
  }

  if (facilitySource === 'none') {
    console.warn('calculatePersonnelAdequacy: no facility data found in scenario');
  }

  // Count MTFs by role using broad matchers (mirrors calculateBedOccupancyAnalysis)
  const isRole1  = (f) => ['ROLE1','ROLE_1'].includes(getField(f));
  const isRole2B = (f) => ['ROLE2_BASIC','ROLE_2_BASIC','ROLE2B','Role2Basic','role2_basic'].includes(getField(f));
  const isRole2E = (f) => ['ROLE2_ENHANCED','ROLE_2_ENHANCED','ROLE2E','Role2Enhanced','role2_enhanced'].includes(getField(f));
  const isRole3  = (f) => ['ROLE3','ROLE_3','Role3','role3'].includes(getField(f));

  const mtfCounts = {
    role1:  facilityList.filter(isRole1).length,
    role2b: facilityList.filter(isRole2B).length,
    role2e: facilityList.filter(isRole2E).length,
    role3:  facilityList.filter(isRole3).length,
  };
  
  /**
   * Get staffing requirements from constants
   * Uses MTF_CAPABILITIES_BY_ECHELON or fallback values that match constants
   */
  const getStaffingRequirements = (roleLevel) => {
    try {
      const mapping = ROLE_TO_ECHELON_MAP[roleLevel];
      if (mapping) {
        const echelonCapabilities = MTF_CAPABILITIES_BY_ECHELON?.[mapping.echelon];
        if (echelonCapabilities) {
          const roleData = echelonCapabilities[mapping.roleKey];
          if (roleData?.personnel_breakdown) {
            const breakdown = roleData.personnel_breakdown;
            return {
              physicians: (breakdown.medical_officers || 0),
              nurses: breakdown.nurses || 0,
              medics: breakdown.medics || 0,
              surgeons: breakdown.surgeons || 0
            };
          }
        }
      }
    } catch (error) {
      console.warn(`Error getting staffing for ${roleLevel}, using fallback`);
    }
    
    // Fallback values (match mtfCapabilities.js constants)
    const FALLBACK_REQUIREMENTS = {
      ROLE_1: { physicians: 2, nurses: 4, medics: 12, surgeons: 0 },
      ROLE_2_BASIC: { physicians: 4, nurses: 20, medics: 24, surgeons: 8 },
      ROLE_2_ENHANCED: { physicians: 6, nurses: 30, medics: 36, surgeons: 12 },
      ROLE_3: { physicians: 8, nurses: 60, medics: 40, surgeons: 16 }
    };
    return FALLBACK_REQUIREMENTS[roleLevel] || FALLBACK_REQUIREMENTS.ROLE_1;
  };
  
  // Get requirements for each role level
  const r1Req = getStaffingRequirements('ROLE_1');
  const r2bReq = getStaffingRequirements('ROLE_2_BASIC');
  const r2eReq = getStaffingRequirements('ROLE_2_ENHANCED');
  const r3Req = getStaffingRequirements('ROLE_3');
  
  // Calculate total required personnel
  const required = {
    surgeons: (mtfCounts.role2b * r2bReq.surgeons) +
              (mtfCounts.role2e * r2eReq.surgeons) +
              (mtfCounts.role3 * r3Req.surgeons),
    physicians: (mtfCounts.role1 * r1Req.physicians) +
                (mtfCounts.role2b * r2bReq.physicians) +
                (mtfCounts.role2e * r2eReq.physicians) +
                (mtfCounts.role3 * r3Req.physicians),
    nurses: (mtfCounts.role1 * r1Req.nurses) +
            (mtfCounts.role2b * r2bReq.nurses) +
            (mtfCounts.role2e * r2eReq.nurses) +
            (mtfCounts.role3 * r3Req.nurses),
    medics: (mtfCounts.role1 * r1Req.medics) +
            (mtfCounts.role2b * r2bReq.medics) +
            (mtfCounts.role2e * r2eReq.medics) +
            (mtfCounts.role3 * r3Req.medics)
  };
  
  // Get current available from scenario (manual fields in Tab 4 Section 4E)
  const personnelConfig = scenario?.logistics?.personnel || scenario?.personnel || {};

  // BUG-S96-013: add medical personnel arriving from outside theater during the operation.
  const opDuration = scenario?.operation_parameters?.duration_days || Infinity;
  const medicalArrivalTotal = (scenario?.logistics?.personnel_arrivals || [])
    .filter(a => a.type === 'MEDICAL' && (a.day || 1) <= opDuration)
    .reduce((sum, a) => sum + (a.quantity || 0), 0);

  // BUG-S96-014: derive organic MTF staffing from the configured facilities.
  // MTF organic staff IS the baseline available pool — manual Tab 4 fields add on top.
  // This mirrors how 'required' is computed: same mtfCounts × staffing ratios.
  const organicAvailable = {
    surgeons:   (mtfCounts.role2b * r2bReq.surgeons)   + (mtfCounts.role2e * r2eReq.surgeons)   + (mtfCounts.role3 * r3Req.surgeons),
    physicians: (mtfCounts.role1 * r1Req.physicians)   + (mtfCounts.role2b * r2bReq.physicians) + (mtfCounts.role2e * r2eReq.physicians) + (mtfCounts.role3 * r3Req.physicians),
    nurses:     (mtfCounts.role1 * r1Req.nurses)       + (mtfCounts.role2b * r2bReq.nurses)     + (mtfCounts.role2e * r2eReq.nurses)     + (mtfCounts.role3 * r3Req.nurses),
    medics:     (mtfCounts.role1 * r1Req.medics)       + (mtfCounts.role2b * r2bReq.medics)     + (mtfCounts.role2e * r2eReq.medics)     + (mtfCounts.role3 * r3Req.medics),
  };

  // Total available = organic (from MTFs) + manual additional + scheduled arrivals
  const available = {
    surgeons:   organicAvailable.surgeons   + (personnelConfig.surgeons   || 0),
    physicians: organicAvailable.physicians + (personnelConfig.physicians || 0),
    nurses:     organicAvailable.nurses     + (personnelConfig.nurses     || 0),
    medics:     organicAvailable.medics     + (personnelConfig.medics     || 0) + medicalArrivalTotal,
  };
  
  // Calculate adequacy for each category
  const categories = ['surgeons', 'physicians', 'nurses', 'medics'];
  const details = categories.map(cat => {
    const req = required[cat] || 0;
    const avail = available[cat] || 0;
    const percent = req > 0 ? Math.round((avail / req) * 100) : 100;
    const shortage = Math.max(0, req - avail);
    const surplus = Math.max(0, avail - req);
    
    return {
      category: cat,
      required: req,
      available: avail,
      percent,
      shortage,
      surplus,
      status: percent >= 100 ? 'ADEQUATE' : percent >= 75 ? 'MARGINAL' : 'CRITICAL'
    };
  });
  
  // Overall status
  const minPercent = Math.min(...details.map(d => d.percent));
  const overallStatus = minPercent >= 100 ? 'ADEQUATE' : minPercent >= 75 ? 'MARGINAL' : 'CRITICAL';
  const criticalShortages = details.filter(d => d.status === 'CRITICAL');
  
  return {
    mtf_counts: mtfCounts,
    total_mtfs: mtfCounts.role1 + mtfCounts.role2b + mtfCounts.role2e + mtfCounts.role3,
    required,
    available,
    organic_available: organicAvailable,        // BUG-S96-014: derived from MTF organic staffing
    medical_arrival_total: medicalArrivalTotal, // BUG-S96-013: external reinforcements
    details,
    overall_percent: Math.round(details.reduce((sum, d) => sum + d.percent, 0) / categories.length),
    overall_status: overallStatus,
    critical_shortages: criticalShortages,
    color: overallStatus === 'ADEQUATE' ? 'success' : overallStatus === 'MARGINAL' ? 'warning' : 'error'
  };
}

/**
 * Validate personnel configuration
 * @param {object} scenario - Complete scenario object
 * @returns {object} Validation results
 */
export function validatePersonnelConfiguration(scenario) {
  const errors = [];
  const warnings = [];

  // Check if personnel section exists
  if (!scenario?.personnel && !scenario?.logistics?.personnel) {
    warnings.push({
      field: 'personnel',
      message: 'Personnel configuration not found',
      severity: 'WARNING'
    });
    return { valid: true, errors, warnings };
  }

  const personnel = scenario.personnel || scenario.logistics?.personnel;

  // Check replacement planning
  if (!personnel.replacement_planning) {
    warnings.push({
      field: 'replacement_planning',
      message: 'No replacement planning configured',
      severity: 'WARNING'
    });
  } else {
    const planning = personnel.replacement_planning;
    
    // Check if guided mode has answers
    if (planning.mode === 'guided' && !planning.guided_answers) {
      warnings.push({
        field: 'guided_answers',
        message: 'Guided mode selected but no answers provided',
        severity: 'WARNING'
      });
    }

    // Check reserve pool
    if (!planning.reserve_pool || 
        (planning.reserve_pool.combat_reserves === 0 && planning.reserve_pool.medical_reserves === 0)) {
      warnings.push({
        field: 'reserve_pool',
        message: 'No reserve pool configured - replacement capability limited',
        severity: 'WARNING'
      });
    }
  }

  // Check mental health configuration
  if (!scenario?.logistics?.mental_health) {
    warnings.push({
      field: 'mental_health',
      message: 'Mental health support not configured',
      severity: 'WARNING'
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

// ============================================================================
// EXPORT ALL
// ============================================================================

const PersonnelIntelligence = {
  calculateMedicalPersonnel,
  calculateMedicalCasualtyRisk,
  calculateRTDProjection,
  calculateCSRProjection,
  generateReplacementRecommendations,
  calculateReservePoolDepletion,
  calculateBedOccupancyAnalysis,
  calculatePersonnelAdequacy,
  validatePersonnelConfiguration,
  // Export mapping for potential external use
  ROLE_TO_ECHELON_MAP
};

export default PersonnelIntelligence;
